// pages/reports/PurchaseOrderReport.jsx
import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  ChevronUp,
  Filter,
  RotateCcw,
  Sliders,
  Eye,
  Package,
  DollarSign,
  //TrendingUp,
  AlertTriangle,
  CheckCircle,
  //XCircle,
  Clock,
  
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { DatePicker } from "antd";
import { purchaseReportActions } from "../../core/action/purchaseReportActions";

const PurchaseOrderReport = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedSort, setSelectedSort] = useState({ value: "product", label: "Product Name" });
  const [searchTerm, setSearchTerm] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0,
    totalTax: 0,
    averageOrder: 0,
    receivedCount: 0,
    pendingCount: 0,
    paidCount: 0,
    partialCount: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const [expandedRows, setExpandedRows] = useState({});

  const sortOptions = [
    { value: "product", label: "Product Name" },
    { value: "sku", label: "SKU" },
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "supplier", label: "Supplier Name" },
    { value: "qty_high", label: "Highest Quantity" },
    { value: "qty_low", label: "Lowest Quantity" }
  ];

  useEffect(() => {
    loadSuppliers();
    loadReport();
  }, []);

  const loadSuppliers = async () => {
    const result = await purchaseReportActions.fetchSuppliers();
    if (result.success) {
      setSuppliers([
        { value: "", label: "All Suppliers" },
        ...result.data.map(supplier => ({
          value: supplier.id,
          label: supplier.supplier_name
        }))
      ]);
    }
  };

  const calculateSummary = (data) => {
    const totalOrders = data.length;
    const totalAmount = data.reduce((sum, item) => sum + (item.total_purchase_amount || 0), 0);
    const totalPaid = data.reduce((sum, item) => sum + (item.total_paid_amount || 0), 0);
    const totalDue = data.reduce((sum, item) => sum + (item.total_due_amount || 0), 0);
    const totalTax = data.reduce((sum, item) => sum + (item.total_tax || 0), 0);
    
    const receivedCount = data.filter(item => item.stock_status === 'in_stock').length;
    const pendingCount = data.filter(item => item.stock_status === 'low_stock').length;
    const paidCount = data.filter(item => item.payment_status === 'paid').length;
    const partialCount = data.filter(item => item.payment_status === 'partial').length;

    setSummary({
      totalOrders,
      totalAmount,
      totalPaid,
      totalDue,
      totalTax,
      averageOrder: totalOrders > 0 ? totalAmount / totalOrders : 0,
      receivedCount,
      pendingCount,
      paidCount,
      partialCount
    });
  };

  const loadReport = async (page = 1) => {
    setLoading(true);
    const filters = {
      page,
      limit: pagination.limit,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      supplierId: selectedSupplier?.value,
      search: searchTerm,
      sort: selectedSort.value
    };

    const result = await purchaseReportActions.fetchReport(filters);
    
    if (result.success) {
      console.log("Report data:", result.data);
      setReportData(result.data || []);
      calculateSummary(result.data || []);
      setPagination({
        page: result.page || 1,
        limit: pagination.limit,
        total: result.total || 0,
        totalPages: result.totalPages || 1
      });
      
      // Reset expanded rows
      setExpandedRows({});
    }
    
    setLoading(false);
  };

  const handleSearch = () => {
    loadReport(1);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedSupplier(null);
    setSearchTerm("");
    setSelectedSort({ value: "product", label: "Product Name" });
    loadReport(1);
  };

  const toggleRow = (productId) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-IN').format(number || 0);
  };

  const getStockStatusBadge = (status, quantity) => {
    if (status === 'out_of_stock' || quantity <= 0) {
      return <span className="badge bg-danger">Out of Stock</span>;
    } else if (status === 'low_stock') {
      return <span className="badge bg-warning text-dark">Low Stock ({quantity})</span>;
    } else {
      return <span className="badge bg-success">In Stock ({quantity})</span>;
    }
  };

  const getStatusIcon = (type) => {
    switch(type) {
      case 'received':
        return <Package size={24} className="text-white" />;
      case 'pending':
        return <Clock size={24} className="text-white" />;
      case 'paid':
        return <CheckCircle size={24} className="text-white" />;
      case 'partial':
        return <AlertTriangle size={24} className="text-white" />;
      default:
        return <DollarSign size={24} className="text-white" />;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Purchase Order Report</h4>
              <h6>Analyze purchase orders and supplier performance</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                <Link onClick={() => loadReport()}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={<Tooltip>Collapse</Tooltip>}>
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        {/* Summary Cards - Top Row */}
        <div className="row mb-4">
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Total Orders</h5>
                <h3 className="mb-0 text-primary">{formatNumber(summary.totalOrders)}</h3>
                <small>All time</small>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Total Amount</h5>
                <h3 className="mb-0 text-primary">{formatCurrency(summary.totalAmount)}</h3>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Total Paid</h5>
                <h3 className="mb-0 text-success">{formatCurrency(summary.totalPaid)}</h3>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Total Due</h5>
                <h3 className="mb-0 text-danger">{formatCurrency(summary.totalDue)}</h3>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Total Tax</h5>
                <h3 className="mb-0 text-warning">{formatCurrency(summary.totalTax)}</h3>
              </div>
            </div>
          </div>
          <div className="col-xl-2 col-sm-4 col-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title text-muted">Avg. Order</h5>
                <h3 className="mb-0 text-info">{formatCurrency(summary.averageOrder)}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Status Summary Cards - Second Row */}
        <div className="row mb-4">
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card bg-success bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Received</h6>
                    <h3 className="mb-0 text-success">{formatNumber(summary.receivedCount)}</h3>
                    <small className="text-muted">Products in stock</small>
                  </div>
                  <div className="bg-success rounded-circle p-3">
                    {getStatusIcon('received')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card bg-warning bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Low Stock</h6>
                    <h3 className="mb-0 text-warning">{formatNumber(summary.pendingCount)}</h3>
                    <small className="text-muted">Need reorder</small>
                  </div>
                  <div className="bg-warning rounded-circle p-3">
                    {getStatusIcon('pending')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card bg-info bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Paid Orders</h6>
                    <h3 className="mb-0 text-info">{formatNumber(summary.paidCount)}</h3>
                    <small className="text-muted">Fully paid</small>
                  </div>
                  <div className="bg-info rounded-circle p-3">
                    {getStatusIcon('paid')}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="card bg-danger bg-opacity-10">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1">Partial Paid</h6>
                    <h3 className="mb-0 text-danger">{formatNumber(summary.partialCount)}</h3>
                    <small className="text-muted">Pending payment</small>
                  </div>
                  <div className="bg-danger rounded-circle p-3">
                    {getStatusIcon('partial')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="card">
          <div className="card-body">
            {/* Search and Filter Bar */}
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by Product Name or SKU"
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Link className="btn btn-searchset" onClick={handleSearch}>
                    <i className="fa fa-search"></i>
                  </Link>
                </div>
              </div>
              <div className="search-path">
                <Link
                  className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                  onClick={() => setIsFilterVisible(!isFilterVisible)}
                >
                  <Filter />
                </Link>
              </div>
              <div className="form-sort">
                <Sliders />
                <Select
                  options={sortOptions}
                  value={selectedSort}
                  onChange={setSelectedSort}
                  isSearchable={false}
                  className="select"
                />
              </div>
            </div>

            {/* Filter Section */}
            {isFilterVisible && (
              <div className="card mt-3">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-3">
                      <div className="mb-3">
                        <label>From Date</label>
                        <DatePicker
                          className="form-control"
                          value={startDate}
                          onChange={setStartDate}
                          format="DD-MM-YYYY"
                          placeholder="From Date"
                        />
                      </div>
                    </div>
                    <div className="col-lg-3">
                      <div className="mb-3">
                        <label>To Date</label>
                        <DatePicker
                          className="form-control"
                          value={endDate}
                          onChange={setEndDate}
                          format="DD-MM-YYYY"
                          placeholder="To Date"
                        />
                      </div>
                    </div>
                    <div className="col-lg-3">
                      <div className="mb-3">
                        <label>Supplier</label>
                        <Select
                          options={suppliers}
                          value={selectedSupplier}
                          onChange={setSelectedSupplier}
                          placeholder="Select Supplier"
                          isClearable
                        />
                      </div>
                    </div>
                    <div className="col-lg-3">
                      <div className="mb-3 d-flex gap-2 mt-4">
                        <button className="btn btn-primary" onClick={handleSearch}>
                          Search
                        </button>
                        <button className="btn btn-secondary" onClick={handleReset}>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Report Table */}
            <div className="table-responsive mt-3">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>In Stock</th>
                    <th>Avg. Price</th>
                    <th>Selling Price</th>
                    <th>Profit Margin</th>
                    <th>Total Purchases</th>
                    <th>Suppliers</th>
                    <th>Last Purchase</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : reportData.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No stock data found
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item) => (
                      <React.Fragment key={item.product_id}>
                        <tr>
                          <td>
                            <strong>{item.product_name}</strong>
                            {item.brand && <div className="text-muted small">{item.brand}</div>}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark">{item.sku}</span>
                          </td>
                          <td>
                            {getStockStatusBadge(item.stock_status, item.total_in_stock)}
                          </td>
                          <td>
                            <div>{formatCurrency(item.avg_unit_price)}</div>
                            <small className="text-muted">
                              Min: {formatCurrency(item.min_unit_price)}<br />
                              Max: {formatCurrency(item.max_unit_price)}
                            </small>
                          </td>
                          <td>
                            <div className="text-info fw-bold">{formatCurrency(item.avg_selling_price)}</div>
                            {item.min_selling_price && (
                              <small className="text-muted">
                                Min: {formatCurrency(item.min_selling_price)}
                              </small>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${
                              item.avg_profit_margin >= 30 ? 'bg-success' : 
                              item.avg_profit_margin >= 20 ? 'bg-info' : 'bg-warning'
                            }`}>
                              {item.avg_profit_margin?.toFixed(2) || '0'}%
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {item.total_purchases} ({item.total_purchased_qty} units)
                            </span>
                          </td>
                          <td>
                            {/* Show supplier names with tooltip if multiple */}
                            <div className="supplier-info">
                              {item.supplier_count > 1 ? (
                                <>
                                  <span className="badge bg-secondary me-1">
                                    {item.supplier_count} Suppliers
                                  </span>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id={`supplier-tooltip-${item.product_id}`}>
                                        <div className="text-start">
                                          <strong>Suppliers:</strong>
                                          <ul className="list-unstyled mt-1 mb-0">
                                            {item.supplier_names?.split(',').map((name, idx) => (
                                              <li key={idx}>• {name.trim()}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      </Tooltip>
                                    }
                                  >
                                    <span className="text-primary" style={{ cursor: 'pointer' }}>
                                      <i className="fa fa-info-circle"></i> View All
                                    </span>
                                  </OverlayTrigger>
                                  <div className="mt-1 small text-muted d-none d-md-block">
                                    {item.supplier_names?.split(',').slice(0, 2).map((name, idx) => (
                                      <span key={idx} className="d-block">
                                        {idx + 1}. {name.trim()}
                                      </span>
                                    ))}
                                    {item.supplier_count > 2 && (
                                      <span className="text-muted">+{item.supplier_count - 2} more</span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <span>{item.supplier_names}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div>{item.last_purchase_date}</div>
                            <small className="text-muted">First: {item.first_purchase_date}</small>
                          </td>
                          <td>
                            <button 
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => toggleRow(item.product_id)}
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                        
                        {/* Details Row */}
                        {expandedRows[item.product_id] && (
                          <tr className="bg-light">
                            <td colSpan="10">
                              <div className="p-3">
                                <div className="row">
                                  <div className="col-md-6">
                                    <h6>Supplier Details</h6>
                                    <table className="table table-sm table-borderless">
                                      <tbody>
                                        {item.supplier_names?.split(',').map((name, idx) => (
                                          <tr key={idx}>
                                            <td width="30%">Supplier {idx + 1}:</td>
                                            <td>
                                              <strong>{name.trim()}</strong>
                                              {item.supplier_ids && (
                                                <span className="text-muted ms-2">
                                                  (ID: {item.supplier_ids.split(',')[idx]?.trim()})
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className="col-md-6">
                                    <h6>Payment Details</h6>
                                    <div className="row">
                                      <div className="col-6">
                                        <small className="text-muted d-block">Total Paid</small>
                                        <strong className="text-success">{formatCurrency(item.total_paid_amount)}</strong>
                                      </div>
                                      <div className="col-6">
                                        <small className="text-muted d-block">Total Due</small>
                                        <strong className="text-danger">{formatCurrency(item.total_due_amount)}</strong>
                                      </div>
                                      <div className="col-6 mt-2">
                                        <small className="text-muted d-block">Payments</small>
                                        <strong>{item.total_payments || 0}</strong>
                                      </div>
                                      <div className="col-6 mt-2">
                                        <small className="text-muted d-block">Last Payment</small>
                                        <strong>
                                          {item.last_payment_date || 'N/A'}
                                          {item.last_payment_method && (
                                            <span className="badge bg-info ms-2">{item.last_payment_method}</span>
                                          )}
                                        </strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {item.reference_nos && item.reference_nos !== 'N/A' && (
                                  <div className="mt-3">
                                    <small className="text-muted">Purchase References:</small>
                                    <div>
                                      {item.reference_nos.split(',').map((ref, idx) => (
                                        <span key={idx} className="badge bg-light text-dark me-2">
                                          {ref.trim()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <nav>
                  <ul className="pagination">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => loadReport(pagination.page - 1)}>
                        Previous
                      </button>
                    </li>
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => loadReport(pageNum)}>
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => loadReport(pagination.page + 1)}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderReport;